import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Save, User, Phone, Mail, MapPin, FileText, ShieldCheck, ExternalLink } from "lucide-react";
import api from "../lib/api";

export default function BusinessProfilePanel({ restaurantSlug }) {
  const [form, setForm] = useState({ name: "", phone: "", company_name: "", vat_number: "", company_address: "" });
  const [initial, setInitial] = useState({ name: "", phone: "", company_name: "", vat_number: "", company_address: "" });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const next = {
          name: data.name || "",
          phone: data.phone || "",
          company_name: data.company_name || "",
          vat_number: data.vat_number || "",
          company_address: data.company_address || "",
        };
        setForm(next); setInitial(next); setEmail(data.email || "");
      } catch { toast.error("Errore caricamento profilo"); }
      finally { setLoading(false); }
    })();
  }, []);

  const dirty = Object.keys(form).some((k) => (form[k] || "").trim() !== (initial[k] || "").trim());
  const filledCount = ["phone", "company_name", "vat_number", "company_address"].filter((k) => (form[k] || "").trim()).length;
  const complete = filledCount === 4;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me/business", form);
      setInitial(form);
      toast.success("Profilo azienda aggiornato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="business-profile-panel" className="bg-white rounded-3xl border border-[#E8E2D5] shadow-sm overflow-hidden">
      <div className="p-5 pb-4 border-b border-[#E8E2D5] flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#3B503A] to-[#2f4030] grid place-items-center text-white shadow"><Building2 size={20}/></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Impostazioni fatturazione</div>
          <h3 className="font-serif-display text-2xl font-bold">Profilo azienda</h3>
        </div>
        {complete ? (
          <span data-testid="business-status-complete" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EAF0E9] text-[#3B503A] text-[11px] font-black uppercase tracking-widest"><ShieldCheck size={11}/> Completo</span>
        ) : (
          <span data-testid="business-status-incomplete" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEF3D6] text-[#8C580C] text-[11px] font-black uppercase tracking-widest">Incompleto · {filledCount}/4</span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {!complete && (
          <div className="rounded-2xl bg-[#FEF3D6]/50 border border-[#D99B26]/30 p-3 text-[12px] text-[#8C580C] leading-relaxed">
            <b>Perché compilare?</b> Questi dati appaiono automaticamente nell'<b>Informativa Privacy</b> pubblica del tuo locale (obbligatoria per il GDPR) e sono usati per la fatturazione degli add-on come Boost Recensioni.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><User size={11}/> Nome legale rappresentante</label>
            <input
              data-testid="business-name-input"
              value={form.name}
              onChange={upd("name")}
              placeholder="Mario Rossi"
              className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><Mail size={11}/> Email account (non modificabile)</label>
            <input value={email} readOnly className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-[#F0EBE1] px-4 py-2.5 text-sm text-[#6B625B] cursor-not-allowed"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><Phone size={11}/> Telefono</label>
            <input
              data-testid="business-phone-input"
              value={form.phone}
              onChange={upd("phone")}
              type="tel"
              inputMode="tel"
              placeholder="+39 333 1234567"
              className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><FileText size={11}/> Partita IVA / Codice Fiscale</label>
            <input
              data-testid="business-vat-input"
              value={form.vat_number}
              onChange={upd("vat_number")}
              placeholder="IT01234567890"
              className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><Building2 size={11}/> Ragione sociale</label>
            <input
              data-testid="business-company-input"
              value={form.company_name}
              onChange={upd("company_name")}
              placeholder="Osteria del Sole S.r.l."
              className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1"><MapPin size={11}/> Indirizzo sede legale</label>
            <input
              data-testid="business-address-input"
              value={form.company_address}
              onChange={upd("company_address")}
              placeholder="Via Roma 12, 20100 Milano (MI)"
              className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            data-testid="business-save-btn"
            onClick={save}
            disabled={loading || saving || !dirty}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white font-black shadow disabled:opacity-50"
          >
            <Save size={14}/> {saving ? "Salvo..." : "Salva profilo"}
          </button>
          {restaurantSlug && (
            <a
              data-testid="business-preview-privacy"
              href={`/privacy/${restaurantSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#E8E2D5] text-[#231F1D] text-sm font-bold hover:bg-[#F0EBE1]"
            >
              <ExternalLink size={13}/> Anteprima informativa
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
